import type { DropdownOption } from "../../../components/ui";
import { attendeeName } from "../appointments";
import type { ClientAppointment } from "../types/appointments.types";

/**
 * The controls the Schedule and Reschedule dialogs are both built out of, and
 * the arithmetic that turns what they hold into the two fields an appointment
 * actually stores.
 *
 * Shared because the two dialogs ask the same three questions — which day,
 * what time, how long — and differ only in what stands around them: one adds
 * a type and a link or an address, the other quotes the appointment being
 * moved. Two copies of a day-and-time form is two places for the two to drift
 * apart on what counts as a valid time.
 */

/** The minutes a slot can fall on, which is what sets the granularity below. */
const QUARTERS = ["00", "15", "30", "45"];

/**
 * Every quarter hour of a day, as `HH:MM` on a 24-hour clock with a
 * twelve-hour label.
 *
 * Quarter-hourly rather than the half-hours `FieldControls` offers for working
 * hours, because these are two different questions. The hours somebody works
 * are a rough shape of a day and are given roughly; an appointment is a slot
 * in a diary, and quarter past is an ordinary time to see somebody. Ninety-six
 * rows is still a list that can be scrolled, where the 1,440 of a
 * minute-by-minute one would be scrolled past rather than read.
 *
 * A dropdown rather than `<input type="time">`, which the operating system
 * draws in its own font with its own idea of whether a day has twelve hours or
 * twenty-four, sitting in a dialog where every other control is this app's.
 */
export const TIME_OPTIONS: DropdownOption[] = Array.from(
  { length: 24 * QUARTERS.length },
  (_, slot) => {
    const hours = Math.floor(slot / QUARTERS.length);
    const minutes = QUARTERS[slot % QUARTERS.length];
    const suffix = hours < 12 ? "AM" : "PM";
    const shown = hours % 12 === 0 ? 12 : hours % 12;

    return {
      value: `${String(hours).padStart(2, "0")}:${minutes}`,
      label: `${String(shown).padStart(2, "0")}:${minutes} ${suffix}`,
    };
  }
);

/**
 * The first slot on the list at or after `now`, as the `HH:MM` the list is
 * keyed by — where the time dropdown opens looking while nothing is chosen.
 *
 * Ninety-six rows is a list that is scrolled, and a day's worth of them
 * starting at midnight put the useful part a third of the way down: every
 * booking began by scrolling past the small hours. Opening at the next
 * quarter hour puts the list where a diary opens, which is now.
 *
 * It only scrolls. The field stays unanswered — see `openAt` on
 * `CustomDropdown` — because an hour nobody chose is not an hour anybody
 * agreed to.
 *
 * The same answer whatever day is being booked. On today it is the next slot
 * that can actually be had; on a day further out it is the same time of day,
 * which is a better place to arrive than midnight and is one rule rather than
 * two. A `now` late enough to run past the end of the day stops at the last
 * slot, since there is nothing after it to open at.
 */
export const nextSlotAfter = (now: Date = new Date()): string => {
  const quarter = 15;
  const minutes = now.getHours() * 60 + Math.ceil(now.getMinutes() / quarter) * quarter;
  // The last row of the list, where rounding up has run off the end of the day.
  const capped = Math.min(minutes, 23 * 60 + 45);

  const pad = (part: number) => String(part).padStart(2, "0");
  return `${pad(Math.floor(capped / 60))}:${pad(capped % 60)}`;
};

/** "45 min", "1 hr", "1 hr 30 min" — the way somebody says a length out loud. */
export function durationOptionLabel(total: number): string {
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (!hours) return `${minutes} min`;
  return minutes ? `${hours} hr ${minutes} min` : `${hours} hr`;
}

/**
 * The lengths an appointment can be booked for, as whole minutes.
 *
 * One list rather than the hours-and-minutes pair both dialogs used to ask
 * for: a consultation is picked off a short list of the lengths a practice
 * actually sells, not assembled out of two halves that then have to be added
 * together. The quarter hours stop at an hour — past that nobody books
 * 2hr 15m — and two hours is the ceiling: a consultation that runs longer
 * than a morning is not one of these, and every row past it was a row that
 * could only be hit by mistake.
 *
 * A length already stored outside this list still shows on the reschedule
 * dialog, which puts the appointment's own length back on — see
 * `durationOptions` there. Shortening this list does not strand an
 * appointment booked under a longer one.
 *
 * Held as minutes, which is already what an appointment stores, so nothing
 * between this list and the payload has to convert anything.
 */
export const DURATION_OPTIONS: DropdownOption[] = [
  15, 30, 45, 60, 90, 120,
].map((total) => ({ value: String(total), label: durationOptionLabel(total) }));

/**
 * What the three controls hold between them, before it is worth anything: the
 * date in the app's `DD/MM/YYYY`, the time as `HH:MM`, and the length as the
 * `DURATION_OPTIONS` value it was picked from — minutes, as a string, because
 * that is what a dropdown holds.
 */
export interface AppointmentWhen {
  date: string;
  time: string;
  duration: string;
}

/** A duration of nothing is not a length, and is what an unanswered field is. */
export const durationOf = (when: AppointmentWhen): number =>
  Number(when.duration) || 0;

/**
 * The day and the time as one moment, or `null` while either is unanswered or
 * impossible.
 *
 * Built out of local parts rather than by parsing a joined string: the fields
 * are filled in by somebody sitting in a timezone, and `new Date("...")` on a
 * date-only string is read as UTC — which would book a late-evening
 * appointment on the wrong day for everybody east of Greenwich.
 *
 * `DD/MM/YYYY` is read back off the parts to reject 31/02, which
 * `new Date(2026, 1, 31)` would quietly roll forward into March. Same check
 * `DatePicker`'s own `parseDisplayDate` makes; repeated rather than imported
 * because this one has a time to fold in as well.
 */
export const startsAtOf = (when: AppointmentWhen): string | null => {
  const day = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(when.date.trim());
  const clock = /^(\d{1,2}):(\d{2})$/.exec(when.time.trim());
  if (!day || !clock) return null;

  const year = Number(day[3]);
  const month = Number(day[2]);
  const date = Number(day[1]);
  const hours = Number(clock[1]);
  const minutes = Number(clock[2]);

  if (hours > 23 || minutes > 59) return null;

  const moment = new Date(year, month - 1, date, hours, minutes, 0, 0);
  if (
    moment.getFullYear() !== year ||
    moment.getMonth() !== month - 1 ||
    moment.getDate() !== date
  ) {
    return null;
  }

  return moment.toISOString();
};

/**
 * Whether the dialog has enough to book with: a real moment, and a length.
 *
 * Whether that moment is allowed is a separate question — see `whenErrors`.
 * Completeness is about the fields being answered; a date that has passed is
 * answered, just refused.
 */
export const isWhenComplete = (when: AppointmentWhen): boolean =>
  !!startsAtOf(when) && durationOf(when) > 0;

/** Midnight on the day `now` falls in — the earliest day that can be picked. */
export const startOfDay = (now: Date = new Date()): Date =>
  new Date(now.getFullYear(), now.getMonth(), now.getDate());

/**
 * What is wrong with the moment the fields describe, field by field, or `{}`
 * where nothing is.
 *
 * Both dialogs refuse a slot that has already passed. The calendar greys those
 * days out, but the field is also typed into — `minDate` only governs what can
 * be clicked — so the same rule is enforced here, where the button reads it.
 *
 * Two messages rather than one, because they are two different mistakes: a day
 * that is behind us, and a time earlier today than right now. A single error
 * under the date field would leave somebody who picked this morning staring at
 * a date that looks perfectly fine.
 *
 * This is where back-dating was given up. An appointment that happened and was
 * never written down has nowhere to go now: the dialog takes future slots
 * only. If that has to come back, it comes back here.
 */
export const whenErrors = (
  when: AppointmentWhen,
  now: Date = new Date()
): { date?: string; time?: string } => {
  const startsAt = startsAtOf(when);
  if (!startsAt) return {};

  const moment = new Date(startsAt);
  if (moment.getTime() >= now.getTime()) return {};

  // Behind us by the clock. Which of the two fields is at fault depends on
  // whether the day itself has gone: a slot at nine this morning is a time
  // that has passed, not a date that has.
  return moment < startOfDay(now)
    ? { date: "This date has already passed." }
    : { time: "This time has already passed today." };
};

/* ---------------------------------------------------------------------------
 * Clashes: whether the slot being asked for is already promised to somebody.
 * ------------------------------------------------------------------------ */

/**
 * The day the date field describes, at local midnight, or `null` while it
 * describes nothing.
 *
 * What the day's bookings are fetched for. Local, not UTC, for the reason
 * `startsAtOf` builds its moment out of parts: a day is the reader's day, and
 * `new Date("2026-10-01")` is midnight in Greenwich, which is the evening of
 * the 30th for anybody west of it.
 */
export const dayOf = (date: string): Date | null => {
  const parts = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(date.trim());
  if (!parts) return null;

  const year = Number(parts[3]);
  const month = Number(parts[2]);
  const day = Number(parts[1]);

  const moment = new Date(year, month - 1, day);
  if (
    moment.getFullYear() !== year ||
    moment.getMonth() !== month - 1 ||
    moment.getDate() !== day
  ) {
    return null;
  }
  return moment;
};

/** When an appointment runs from and to, in milliseconds. */
const spanOf = (
  appointment: Pick<ClientAppointment, "startsAt" | "durationMinutes">
): { from: number; to: number } | null => {
  const from = new Date(appointment.startsAt).getTime();
  if (Number.isNaN(from)) return null;

  // A row with no length still occupies its start. Treated as a moment rather
  // than skipped, so a booking the backend stored without an `ends_at` cannot
  // be quietly booked over.
  return { from, to: from + Math.max(appointment.durationMinutes, 0) * 60_000 };
};

/**
 * The appointment the fields would collide with, or `null` where the slot is
 * free.
 *
 * Two spans overlap when each starts before the other ends. Touching ends do
 * not collide: an appointment finishing at 11:00 and one starting at 11:00 are
 * back to back, which is a full diary rather than a double booking, and a
 * dialog that refused it would make an ordinary day impossible to book.
 *
 * `ignoreId` is the appointment being moved. Reschedule opens on the slot the
 * appointment already occupies, so without it every reschedule dialog would
 * open reporting a clash with itself.
 */
export const clashWith = (
  when: AppointmentWhen,
  booked: ClientAppointment[],
  ignoreId?: string
): ClientAppointment | null => {
  const startsAt = startsAtOf(when);
  const minutes = durationOf(when);
  if (!startsAt || minutes <= 0) return null;

  const wanted = spanOf({ startsAt, durationMinutes: minutes });
  if (!wanted) return null;

  return (
    booked.find((appointment) => {
      if (appointment.id === ignoreId) return null;
      const span = spanOf(appointment);
      return !!span && wanted.from < span.to && span.from < wanted.to;
    }) ?? null
  );
};

/** "11:45 AM" — the way the disabled rows below quote an hour. */
const clockLabel = (at: number): string => {
  const moment = new Date(at);
  const hours = moment.getHours();
  const suffix = hours < 12 ? "AM" : "PM";
  const shown = hours % 12 === 0 ? 12 : hours % 12;
  return `${String(shown).padStart(2, "0")}:${String(moment.getMinutes()).padStart(
    2,
    "0"
  )} ${suffix}`;
};

/**
 * The time list with every hour that is already spoken for closed off.
 *
 * This is the whole of how a clash is reported. It was an error message under
 * the field once, and a panel listing the day's bookings under that — both of
 * which let somebody pick a time, read that it was refused, and pick again.
 * Refusing before the press is fewer words and less work: the hours that
 * cannot be had are struck through on the list, they say whose they are, and
 * they cannot be chosen.
 *
 * A start is closed off when the appointment it would begin — this start, for
 * `durationMinutes` — runs over one already booked. Which means the list
 * changes with the length: 11:00 is free for fifteen minutes and taken for an
 * hour, and that is the truth of the diary rather than an inconsistency.
 *
 * Before a length has been picked, a start is closed off only where it lands
 * inside a booking. That much is knowable without one, and it is the case
 * worth catching early — a start in the middle of somebody else's hour is
 * wrong at every length.
 *
 * Touching ends do not collide, here as in `clashWith`: an appointment
 * finishing at 11:00 leaves 11:00 free to start.
 */
export const timeOptionsWithBookings = (
  options: DropdownOption[],
  date: string,
  durationMinutes: number,
  booked: ClientAppointment[],
  ignoreId?: string
): DropdownOption[] => {
  const day = dayOf(date);
  if (!day || !booked.length) return options;

  const spans = booked
    .filter((appointment) => appointment.id !== ignoreId)
    .map((appointment) => ({ appointment, span: spanOf(appointment) }))
    .filter(
      (entry): entry is { appointment: ClientAppointment; span: { from: number; to: number } } =>
        !!entry.span
    );

  if (!spans.length) return options;

  const minutes = Math.max(durationMinutes, 0);

  return options.map((option) => {
    const clock = /^(\d{1,2}):(\d{2})$/.exec(option.value);
    if (!clock) return option;

    const from = new Date(
      day.getFullYear(),
      day.getMonth(),
      day.getDate(),
      Number(clock[1]),
      Number(clock[2])
    ).getTime();
    // A length of nothing makes the start a moment rather than a stretch, so
    // only a start that falls inside a booking is closed off.
    const to = from + minutes * 60_000;

    const taken = spans.find(
      (entry) =>
        (from < entry.span.to && entry.span.from < to) ||
        (minutes === 0 && from >= entry.span.from && from < entry.span.to)
    );
    if (!taken) return option;

    // Named whoever the hour is already promised to, client or enquiry: the
    // practitioner cannot be in two places, and "Already scheduled" with
    // nobody's name on it is the version somebody has to go hunting after.
    const who = attendeeName(taken.appointment);
    const span = `${clockLabel(taken.span.from)} – ${clockLabel(taken.span.to)}`;

    return {
      ...option,
      disabled: true,
      disabledReason: who
        ? `Already scheduled · ${who}, ${span}`
        : `Already scheduled · ${span}`,
    };
  });
};
